/**
 * Dragon-tier victory overlay — festive celebration + optional feedback segments.
 */
import { mergeTierImageUrl } from "../assets/merge-tier-urls.js";

export var VICTORY_POPUP_TIMING = {
  /** Delay after merge / discovery ceremony before the overlay opens. */
  SETTLE_MS: 300,
  /** Fade-out duration when the player continues. */
  DISMISS_MS: 450,
  /** Fallback teardown delay — keep in sync with `victory-overlay-fade-out`. */
  DISMISS_FALLBACK_MS: 482,
};

/** Feedback scale — keep in sync with analytics `content_desire` and player-stats clamp. */
export var VICTORY_CONTENT_DESIRE = {
  MIN: 0,
  MAX: 10,
  NEUTRAL: 5,
};

/** Three-segment picker labels map to low / neutral / high on the 0–10 scale. */
export var VICTORY_FEEDBACK_SEGMENTS = [
  { value: 0, label: "No", assetId: "snail" },
  { value: 5, label: "Maybe", assetId: "bug" },
  { value: 10, label: "Yes!", assetId: "butterfly" },
];

/**
 * @param {HTMLElement} root
 * @param {{ onChange?: (value: number) => void }} [opts]
 * @returns {{ getValue: () => number, wasMoved: () => boolean, destroy: () => void, focus: () => void }}
 */
export function createVictoryFeedbackSegments(root, opts) {
  opts = opts || {};
  var selectedIndex = -1;
  var moved = false;
  var buttons = [];

  root.innerHTML = ""
    + "<div class=\"victory-feedback-segments\" role=\"radiogroup\""
    + " aria-label=\"Should we make part 2?\" tabindex=\"0\">"
    + VICTORY_FEEDBACK_SEGMENTS.map(function (segment, index) {
      var iconUrl = mergeTierImageUrl(segment.assetId);
      return ""
        + "<button type=\"button\" class=\"victory-feedback-segments__btn\""
        + " role=\"radio\" aria-checked=\"false\" aria-label=\"" + segment.label + "\""
        + " data-index=\"" + index + "\">"
        + "  <img class=\"victory-feedback-segments__icon\" src=\"" + iconUrl + "\" alt=\"\" draggable=\"false\" />"
        + "  <span class=\"victory-feedback-segments__label\">" + segment.label + "</span>"
        + "</button>";
    }).join("")
    + "</div>";

  var group = root.querySelector(".victory-feedback-segments");
  if (!group) {
    return {
      getValue: function () { return VICTORY_CONTENT_DESIRE.NEUTRAL; },
      wasMoved: function () { return false; },
      destroy: function () { root.innerHTML = ""; },
      focus: function () {},
    };
  }

  buttons = Array.prototype.slice.call(group.querySelectorAll(".victory-feedback-segments__btn"));

  function notify() {
    if (typeof opts.onChange === "function") {
      opts.onChange(getValue());
    }
  }

  function getValue() {
    if (selectedIndex < 0) return VICTORY_CONTENT_DESIRE.NEUTRAL;
    return VICTORY_FEEDBACK_SEGMENTS[selectedIndex].value;
  }

  function applySelection() {
    for (var i = 0; i < buttons.length; i++) {
      var selected = i === selectedIndex;
      buttons[i].classList.toggle("victory-feedback-segments__btn--selected", selected);
      buttons[i].setAttribute("aria-checked", selected ? "true" : "false");
    }
  }

  function selectIndex(index, fromUser) {
    var next = Math.max(0, Math.min(VICTORY_FEEDBACK_SEGMENTS.length - 1, index));
    if (next === selectedIndex && fromUser) {
      moved = true;
      notify();
      return;
    }
    if (next === selectedIndex) return;
    selectedIndex = next;
    if (fromUser) moved = true;
    applySelection();
    notify();
  }

  function onSegmentClick(ev) {
    var btn = ev.currentTarget;
    var index = Number(btn.getAttribute("data-index"));
    if (!isFinite(index)) return;
    selectIndex(index, true);
  }

  function onGroupKeyDown(ev) {
    if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
    ev.preventDefault();
    var start = selectedIndex >= 0 ? selectedIndex : 1;
    var delta = ev.key === "ArrowLeft" ? -1 : 1;
    selectIndex(start + delta, true);
  }

  for (var b = 0; b < buttons.length; b++) {
    buttons[b].addEventListener("click", onSegmentClick);
  }
  group.addEventListener("keydown", onGroupKeyDown);

  applySelection();

  return {
    getValue: getValue,
    wasMoved: function () { return moved; },
    destroy: function () {
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].removeEventListener("click", onSegmentClick);
      }
      group.removeEventListener("keydown", onGroupKeyDown);
      root.innerHTML = "";
    },
    focus: function () {
      if (selectedIndex >= 0 && buttons[selectedIndex]) {
        buttons[selectedIndex].focus();
        return;
      }
      if (typeof group.focus === "function") group.focus();
    },
  };
}

/**
 * @param {object} opts
 * @param {HTMLElement} opts.overlay
 * @param {HTMLElement} opts.dragonImg
 * @param {HTMLElement} opts.headline
 * @param {HTMLElement} opts.feedbackBlock
 * @param {HTMLElement} opts.feedbackMount
 * @param {HTMLButtonElement} opts.continueBtn
 * @param {boolean} opts.isFirstUnlock
 * @param {boolean} opts.showFeedback
 * @param {() => void} [opts.onPlaySound]
 * @param {(result: { value: number, moved: boolean }) => void} opts.onContinue
 * @returns {{ close: () => void, abort: () => void, isActive: () => boolean }}
 */
export function showVictoryOverlay(opts) {
  var overlay = opts.overlay;
  var feedback = null;
  var closed = false;
  var closing = false;
  var dismissTimer = null;
  var onDismissAnimEnd = null;
  var onContinue = opts.onContinue;

  function cancelDismissAnimation() {
    if (dismissTimer) {
      clearTimeout(dismissTimer);
      dismissTimer = null;
    }
    if (onDismissAnimEnd) {
      overlay.removeEventListener("animationend", onDismissAnimEnd);
      onDismissAnimEnd = null;
    }
  }

  function teardownDom() {
    if (feedback) {
      feedback.destroy();
      feedback = null;
    }
    overlay.classList.remove(
      "victory-overlay--visible",
      "victory-overlay--repeat",
      "victory-overlay--closing"
    );
    overlay.classList.add("hidden");
    overlay.setAttribute("aria-hidden", "true");
    opts.headline.textContent = "";
    opts.feedbackBlock.classList.add("hidden");
    opts.feedbackMount.innerHTML = "";
    opts.continueBtn.disabled = false;
    overlay.removeEventListener("click", onOverlayClick);
    opts.continueBtn.removeEventListener("click", onContinueClick);
  }

  function cleanup() {
    closed = true;
    closing = false;
    cancelDismissAnimation();
    teardownDom();
  }

  function finishDismiss(result) {
    cleanup();
    if (typeof onContinue === "function") onContinue(result);
  }

  function dismissWithFade(result) {
    if (closed || closing) return;
    closing = true;
    opts.continueBtn.disabled = true;
    cancelDismissAnimation();

    if (typeof overlay.offsetWidth === "number") {
      void overlay.offsetWidth;
    }

    var done = false;
    var finishOnce = function () {
      if (done) return;
      done = true;
      finishDismiss(result);
    };

    onDismissAnimEnd = function (ev) {
      if (ev.target !== overlay) return;
      if (ev.animationName !== "victory-overlay-fade-out") return;
      finishOnce();
    };

    if (typeof overlay.addEventListener === "function") {
      overlay.addEventListener("animationend", onDismissAnimEnd);
    }
    dismissTimer = setTimeout(finishOnce, VICTORY_POPUP_TIMING.DISMISS_FALLBACK_MS);
    overlay.classList.add("victory-overlay--closing");
  }

  function onContinueClick() {
    if (closed || closing) return;
    var result = {
      value: feedback ? feedback.getValue() : VICTORY_CONTENT_DESIRE.NEUTRAL,
      moved: feedback ? feedback.wasMoved() : false,
    };
    dismissWithFade(result);
  }

  function onOverlayClick(ev) {
    if (!opts.showFeedback && ev.target === overlay.querySelector(".victory-overlay__vignette")) {
      onContinueClick();
    }
  }

  var headlineText = opts.isFirstUnlock ? "DRAGON UNLEASHED!" : "THE DRAGON RETURNS!";
  opts.headline.textContent = "";
  opts.headline.setAttribute("aria-label", headlineText);
  var words = headlineText.split(/\s+/).filter(Boolean);
  for (var w = 0; w < words.length; w++) {
    if (w > 0) {
      opts.headline.appendChild(document.createTextNode(" "));
    }
    var wordSpan = document.createElement("span");
    wordSpan.className = "victory-overlay__headline-word";
    wordSpan.textContent = words[w];
    wordSpan.style.animationDelay = (0.8 + w * 0.14) + "s";
    opts.headline.appendChild(wordSpan);
  }

  opts.dragonImg.src = mergeTierImageUrl("dragon_final");
  opts.dragonImg.alt = "Dragon";

  if (opts.showFeedback) {
    opts.feedbackBlock.classList.remove("hidden");
    feedback = createVictoryFeedbackSegments(opts.feedbackMount);
  } else {
    opts.feedbackBlock.classList.add("hidden");
    overlay.classList.add("victory-overlay--repeat");
  }

  overlay.classList.remove("hidden");
  overlay.setAttribute("aria-hidden", "false");
  requestAnimationFrame(function () {
    overlay.classList.add("victory-overlay--visible");
    if (feedback && typeof feedback.focus === "function") feedback.focus();
  });

  if (typeof opts.onPlaySound === "function") opts.onPlaySound();

  overlay.addEventListener("click", onOverlayClick);
  opts.continueBtn.addEventListener("click", onContinueClick);

  return {
    close: onContinueClick,
    abort: function () {
      cancelDismissAnimation();
      cleanup();
    },
    isActive: function () { return !closed && !closing; },
  };
}
